<template>
  <v-dialog v-model="dialog" max-width="800px" persistent scrollable @keydown.esc="cancel">
    <v-card>
      <v-card-title>
        <span class="headline">{{ editingRule ? 'Edit Category Rule' : 'Create Category Rule' }}</span>
      </v-card-title>

      <v-card-text>
        <v-container>
          <!-- Debug info - remove in production -->
          <v-alert v-if="debugMode" type="info" class="mb-4">
            <pre>{{ JSON.stringify(editingRule, null, 2) }}</pre>
          </v-alert>

          <v-text-field
            v-model="localRule.name"
            label="Category Name"
            placeholder="e.g., Men, Clothing, Cashmere Tops"
            required
            class="mb-6"
            variant="outlined"
            density="compact"
            autocomplete="off"
            hide-details
          ></v-text-field>

          <!-- UPDATED: Multiple Web Category Codes Field -->
          <div class="mb-6">
            <label class="v-label text-subtitle-2 font-weight-medium mb-2 d-block">Web Category Codes</label>
            <p class="text-caption text-grey-darken-1 mb-3">
              Add one or more web category codes for this category. These codes are used for frontend routing and SEO.
            </p>
            
            <!-- Display existing codes as chips -->
            <div v-if="localRule.web_category_codes && localRule.web_category_codes.length > 0" class="mb-3">
              <v-chip
  v-for="(code, index) in localRule.web_category_codes"
  :key="code"  
  class="mr-2 mb-2"
  closable
  @click:close="removeWebCategoryCode(index)"
>
  {{ code }}
</v-chip>
            </div>

            <!-- Input for adding new codes -->
            <div class="d-flex align-center gap-2">
              <v-combobox
  v-model="newWebCategoryCode"
  :items="availableWebCategoryCodes"
  label="Add web category code"
  placeholder="e.g., women-clothing-accessories"
  clearable
  variant="outlined"
  density="compact"
  hide-details
  class="flex-grow-1"
  @keydown.enter="addWebCategoryCode"
  @update:modelValue="onWebCategoryCodeChange"
@update:menu="onMenuStateChange"
></v-combobox>
              <v-btn
                color="primary"
                :disabled="!newWebCategoryCode || !isValidWebCategoryCode(newWebCategoryCode)"
                @click="addWebCategoryCode"
                size="small"
              >
                Add
              </v-btn>
            </div>
            
            <div v-if="newWebCategoryCode && !isValidWebCategoryCode(newWebCategoryCode)" class="text-error text-caption mt-1">
              Code should contain only lowercase letters, numbers, hyphens, and underscores
            </div>
          </div>
          <!-- END UPDATED -->

          <div class="mb-4">
            <h3 class="mb-2 text-subtitle-1 font-weight-medium">Definitional Conditions (Optional)</h3>
            <p class="text-caption text-grey-darken-1 mb-3">
              Define which products belong to this category based on their attributes. These attributes determine WHO gets into the category.
            </p>
            <v-alert type="info" density="compact" class="mb-3" variant="tonal">
              <strong>ℹ️ Note:</strong> Only base product-level attributes are shown. Variant-level attributes are hidden as they cannot be used in frontend taxonomy rules.
            </v-alert>
            <div v-if="localRule.conditions && localRule.conditions.groups && localRule.conditions.groups.length > 0" 
     :key="dialogRenderKey">
  <div v-for="(group, groupIndex) in localRule.conditions.groups" :key="group.id">
              <v-row v-if="groupIndex > 0" class="align-center my-3">
                <v-col><v-divider></v-divider></v-col>
                <v-col cols="auto" class="px-1">
                  <v-select
                    v-model="localRule.conditions.operator"
                    :items="['AND', 'OR']"
                    density="compact"
                    variant="outlined"
                    hide-details
                    style="min-width: 80px;"
                  ></v-select>
                </v-col>
                <v-col><v-divider></v-divider></v-col>
              </v-row>

              <div class="pa-4 mt-2 condition-group">
                <div class="d-flex justify-space-between align-center mb-2">
                  <v-select
                    v-model="group.operator"
                    :items="['AND', 'OR']"
                    label="Group Logic"
                    density="compact"
                    variant="outlined"
                    hide-details
                    style="max-width: 120px;"
                  ></v-select>
                  <v-btn
                    icon="mdi-close"
                    size="x-small"
                    variant="text"
                    color="grey"
                    @click="removeGroup(groupIndex)"
                    title="Remove this group"
                  ></v-btn>
                </div>

                <div v-for="(condition, condIndex) in group.conditions" :key="`${group.id}-${condition.id}`" class="d-flex align-start mb-3 condition-row">
                  <span v-if="condIndex > 0" class="text-grey-darken-1 mx-2 mt-2 font-weight-bold">{{ group.operator }}</span>

                  <v-autocomplete
                    :class="{'ml-8': condIndex === 0}"
                    attach
                    autocomplete="off"
                    :items="attributeItems"
                    item-title="name"
                    item-value="id"
                    v-model="condition.attribute_id"
                    label="Attribute"
                    density="compact"
                    hide-details
                    class="mr-2"
                    @update:modelValue="onAttributeChange(condition)"
                  ></v-autocomplete>

                  <v-select
                    attach
                    :items="['is', 'is one of']"
                    v-model="condition.operator"
                    density="compact"
                    hide-details
                    class="mr-2"
                    style="max-width: 140px;"
                    @update:modelValue="onOperatorChange(condition)"
                  ></v-select>

<!-- For single selection -->
<v-autocomplete
  v-if="condition.operator === 'is' || condition.operator === 'equals'"
  :key="`single-${condition.id}-${condition.attribute_id}`"
  attach
  autocomplete="off"
  v-model="condition.value_id" 
  :items="getValueItems(condition.attribute_id)"
  item-title="title" 
  item-value="value" 
  label="Value"
  density="compact"
  hide-details
  :disabled="!condition.attribute_id"
  no-data-text="No values available"
></v-autocomplete>

<!-- For multiple selection -->
<v-autocomplete
  v-else-if="condition.operator === 'is one of' || condition.operator === 'in'"
  :key="`multiple-${condition.id}`"
  v-model="condition.value_id"
  :items="getValueItems(condition.attribute_id)"
  item-title="title"
  item-value="value"
  label="Value"
  density="compact"
  hide-details
  :disabled="!condition.attribute_id"
  multiple
  chips
  closable-chips
  :return-object="false"
  class="stable-chips"
></v-autocomplete>

                  <v-btn icon="mdi-delete" size="small" variant="text" color="error" @click="removeCondition(groupIndex, condIndex)"></v-btn>
                </div>
                  <v-btn color="secondary" variant="text" size="small" @click="addCondition(group)">
                  <v-icon left>mdi-plus</v-icon>Add {{ group.operator }} Condition
                </v-btn>
              </div>
            </div>

            <v-btn color="primary" block class="mt-6" variant="tonal" @click="addGroup">
              <v-icon left>mdi-plus</v-icon>Add {{ localRule.conditions.operator }} Group
            </v-btn>
            </div>
            <v-btn color="secondary" variant="tonal" @click="ensureConditions" v-else>
              <v-icon left>mdi-plus</v-icon>Add Definitional Conditions
            </v-btn>
          </div>

          <v-divider class="my-6"></v-divider>

          <div>
            <h3 class="mb-2 text-subtitle-1 font-weight-medium">Available Attributes (Optional)</h3>
            <p class="text-caption text-grey-darken-1 mb-3">
              Link this category to backend taxonomy paths. Products in this category will inherit all attributes from these backend categories for filtering and description purposes.
            </p>

            <div v-if="localRule.backend_category_ids && localRule.backend_category_ids.length > 0">
  <div v-for="(categoryId, index) in localRule.backend_category_ids" :key="categoryId" class="pa-3 mb-3 category-item">
    <div class="d-flex align-center">
      <div class="flex-grow-1">
        <p class="font-weight-bold mb-1">{{ getCategoryDisplay(categoryId) }}</p>
        <p class="text-caption text-grey-darken-1">
          Inherits: {{ getInheritedAttributeNames(categoryId).slice(0, 3).join(', ') }}
          <span v-if="getInheritedAttributeNames(categoryId).length > 3">
            + {{ getInheritedAttributeNames(categoryId).length - 3 }} more
          </span>
        </p>
      </div>
      <v-btn icon="mdi-delete" size="small" variant="text" color="error" @click="removeBackendCategory(index)"></v-btn>
    </div>
  </div>
</div>

<div class="d-flex align-center gap-2">
  <v-autocomplete
    label="Select Backend Category"
    v-model="selectedCategoryId"
    :items="backendCategoryItems"
    item-title="title"
    item-value="id"
    placeholder="Search or select a backend category"
    clearable
    hide-details
    variant="outlined"
    density="compact"
    class="flex-grow-1"
  ></v-autocomplete>
  
  <v-checkbox
    v-model="includeChildCategories"
    label="+ children"
    hide-details
    density="compact"
    style="flex-shrink: 0;"
    :disabled="!selectedCategoryId"
  ></v-checkbox>
  
  <v-btn
    color="primary"
    :disabled="!selectedCategoryId"
    @click="addBackendCategory"
    size="small"
  >
    Add
  </v-btn>
</div>

<div v-if="selectedCategoryId && includeChildCategories" class="mt-2">
  <v-chip-group>
    <v-chip
      v-for="childId in getChildCategoryIds(selectedCategoryId)"
      :key="childId"
      size="small"
      color="blue-grey"
      variant="outlined"
    >
      {{ getCategoryDisplay(childId).split(' > ').pop() }}
    </v-chip>
  </v-chip-group>
  <p class="text-caption text-grey-darken-1 mt-1">
    {{ getChildCategoryIds(selectedCategoryId).length }} child categories will be added
  </p>
</div>
          </div>
        </v-container>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="cancel">Cancel</v-btn>
        <v-btn color="primary" @click="save" :disabled="!isRuleValid">Save Rule</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>

export default {
  name: 'RuleBuilderDialog',
  props: {
    show: { type: Boolean, required: true },
    attributes: { type: Object, required: true },
    backendCategoryPaths: { type: Array, default: () => [] },
    backendTaxonomyData: { type: Object, default: () => ({}) },
    editingRule: { type: Object, default: null },
    debugMode: { type: Boolean, default: false },
    allWebCategoryCodes: { type: Array, default: () => [] }
  },
  emits: ['update:show', 'save'],
  data() {
    return {
      dialog: this.show,
      localRule: this.getEmptyRule(),
      categoryDisplayInfo: {}, // Populated from backend taxonomy
      selectedCategoryId: null, // Temporary holder for category selection
      includeChildCategories: false,
      dialogRenderKey: 0,
      menuWasOpen: false,
      newWebCategoryCode: null // NEW: for adding web category codes
    };
  },
  computed: {
    attributeItems() {
      if (!this.attributes) return [];
      return Object.values(this.attributes)
        .filter(attr => {
          // Only show base product-level attributes (exclude variant-level)
          // Frontend taxonomy matching only works with base product attributes
          return attr.attribute_level !== 'Variant';
        })
        .map(attr => {
          const displayName = attr.display_name?.en || attr.name || `Unnamed ${attr.attribute_id}`;
          const internalName = attr.name || null;
          // Show internal name in brackets if it differs from display name
          const name = (internalName && internalName !== displayName) 
            ? `${displayName} [${internalName}]` 
            : displayName;
          return {
            id: attr.attribute_id,
            name,
          };
        })
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    isRuleValid() {
      return !!(this.localRule.name && this.localRule.name.trim());
    },
    conditionsKey() {
      if (!this.localRule.conditions || !this.localRule.conditions.groups) return 'empty';
      return this.localRule.conditions.groups.map(group =>
        group.conditions.map(c => `${c.id}-${c.attribute_id}-${c.operator}-${JSON.stringify(c.value)}`).join('|')
      ).join('||');
    },
    // NEW: Available web category codes (excluding already selected ones)
    availableWebCategoryCodes() {
      const existing = this.localRule.web_category_codes || [];
      return this.allWebCategoryCodes.filter(code => !existing.includes(code));
    },
    backendCategoryItems() {
    if (!this.categoryDisplayInfo || Object.keys(this.categoryDisplayInfo).length === 0) {
      return [];
    }
    
    return Object.entries(this.categoryDisplayInfo)
      .map(([id, info]) => ({
        id: id,
        title: info.path,
        name: info.name
      }))
      .sort((a, b) => a.title.localeCompare(b.title));
  },
  
  // Get children of selected category
  childCategoryIds() {
    if (!this.selectedCategoryId || !this.backendTaxonomyData) return [];
    
    const selectedNode = this.findNodeById(this.selectedCategoryId);
    if (!selectedNode || !selectedNode.subcategories) return [];
    
    const childIds = [];
    function collectChildIds(node) {
      if (node.subcategories && Array.isArray(node.subcategories)) {
        node.subcategories.forEach(child => {
          if (child.id) {
            childIds.push(child.id);
            collectChildIds(child); // Recursively get all descendants
          }
        });
      }
    }
    collectChildIds(selectedNode);
    
    return childIds;
  }
  },
  watch: {
  show: {
    handler(newVal) {
      this.dialog = newVal;
      if (newVal) {
        this.$nextTick(() => {
          this.initializeState();
          this.loadCategoryDisplayInfo(); // Add this to load display info when dialog opens
        });
      }
    },
    immediate: true
  },
  
  editingRule: {
    handler(newVal) {
      if (this.dialog && newVal !== null) {
        this.$nextTick(() => {
          this.initializeState();
        });
      }
    },
    deep: true,
    immediate: true
  },
  
  dialog(newVal) {
    if (!newVal) {
      this.$emit('update:show', false);
    }
  },
  
  // CHANGED: Watch selectedCategoryId instead of newBackendCategoryPath
  selectedCategoryId(newVal) {
    if (!newVal) {
      this.includeChildCategories = false;
    }
  },
  
  // OPTIONAL: Add watcher for backendTaxonomyData if it might change
  backendTaxonomyData: {
    handler(newVal) {
      if (newVal) {
        this.loadCategoryDisplayInfo();
      }
    },
    deep: false
  }
},
  mounted() {
    this.originalError = window.onerror;
    window.onerror = (message, source, lineno, colno, error) => {
      if (message.includes('ResizeObserver loop completed with undelivered notifications')) {
        console.warn('ResizeObserver error suppressed (known Vuetify issue)');
        return true;
      }
      if (this.originalError) {
        return this.originalError(message, source, lineno, colno, error);
      }
      return false;
    };
  },
  beforeUnmount() {
    if (this.originalError) {
      window.onerror = this.originalError;
    } else {
      window.onerror = null;
    }
  },
  methods: {
    getEmptyRule() {
      return {
        id: null,
        name: '',
        type: 'conditional',
        web_category_codes: [], // CHANGED: from single string to array
        conditions: { operator: 'AND', groups: [] },
        backend_category_ids: [],
        children: [],
      };
    },
    loadCategoryDisplayInfo() {
    if (this.backendTaxonomyData) {
      // Build ID to path/name mapping
      const displayInfo = {};
      
      function traverse(node, currentPath = []) {
        if (!node || !node.name) return;
        
        const newPath = [...currentPath, node.name];
        
        if (node.id) {
          displayInfo[node.id] = {
            name: node.name,
            path: newPath.join(' > ')
          };
        }
        
        if (Array.isArray(node.subcategories)) {
          node.subcategories.forEach(sub => traverse(sub, newPath));
        }
      }
      
      Object.values(this.backendTaxonomyData).forEach(topLevelNode => traverse(topLevelNode, []));
      this.categoryDisplayInfo = displayInfo;
    }
  },
  initializeState() {
    console.log('Initializing state with editingRule:', this.editingRule);

    if (this.editingRule && typeof this.editingRule === 'object') {
      try {
        this.localRule = JSON.parse(JSON.stringify(this.editingRule));

        if (!this.localRule.type) this.localRule.type = 'conditional';
        
        // Ensure we have backend_category_ids array (ID-based)
        if (!this.localRule.backend_category_ids) {
          this.localRule.backend_category_ids = [];
        }
        
        // Handle web category codes
        if (!this.localRule.web_category_codes) {
          this.localRule.web_category_codes = [];
        }
        
        // Migration: If there's a single web_category_code, move it to array
        if (this.localRule.web_category_code && typeof this.localRule.web_category_code === 'string') {
          if (!this.localRule.web_category_codes.includes(this.localRule.web_category_code)) {
            this.localRule.web_category_codes.push(this.localRule.web_category_code);
          }
          delete this.localRule.web_category_code;
        }

        // Remove any legacy path-based fields
        delete this.localRule.backend_categories;
        delete this.localRule.linked_categories;
        delete this.localRule.backend_path;
        delete this.localRule.attribute_ids;

        // Handle conditions structure
        if (!this.localRule.conditions || typeof this.localRule.conditions !== 'object') {
          this.localRule.conditions = { operator: 'AND', groups: [] };
        } else {
          if (!this.localRule.conditions.operator) {
            this.localRule.conditions.operator = 'AND';
          }
          if (!Array.isArray(this.localRule.conditions.groups)) {
            this.localRule.conditions.groups = [];
          }
        }

        // Process condition groups (existing logic)
        if (this.localRule.conditions.groups && Array.isArray(this.localRule.conditions.groups)) {
          this.localRule.conditions.groups.forEach((group, groupIndex) => {
            if (!group.id) {
              group.id = `group_${Date.now()}_${groupIndex}`;
            }
            if (!group.operator) {
              group.operator = 'OR';
            }
            if (!Array.isArray(group.conditions)) {
              group.conditions = [];
            }

            group.conditions.forEach((condition, condIndex) => {
              if (!condition.id) {
                condition.id = `cond_${Date.now()}_${groupIndex}_${condIndex}`;
              }
              
              // Convert operators for UI
              if (condition.operator === 'equals') {
                condition.operator = 'is';
              } else if (condition.operator === 'in') {
                condition.operator = 'is one of';
              }

              // Ensure value is properly formatted
              if (condition.operator === 'is one of' && !Array.isArray(condition.value_id)) {
                condition.value_id = condition.value_id ? [condition.value_id] : [];
              } else if (condition.operator === 'is' && Array.isArray(condition.value_id)) {
                condition.value_id = condition.value_id.length > 0 ? condition.value_id[0] : null;
              }
            });
          });
        }

      } catch (error) {
        console.error('Error initializing rule state:', error);
        this.localRule = this.getEmptyRule();
      }
    } else {
      this.localRule = this.getEmptyRule();
    }

    // Reset selection state
    this.selectedCategoryId = null;
    this.includeChildCategories = false;
    this.newWebCategoryCode = null;
    
    // Load display info if not already loaded
    if (!this.categoryDisplayInfo || Object.keys(this.categoryDisplayInfo).length === 0) {
      this.loadCategoryDisplayInfo();
    }
    
    this.$forceUpdate();
  },

  // Updated addBackendCategory method using IDs
  addBackendCategory() {
    console.log('[ADD BACKEND DEBUG] Method called');
    console.log('[ADD BACKEND DEBUG] selectedCategoryId:', this.selectedCategoryId);
    
    const categoryId = this.selectedCategoryId;
    if (!categoryId) {
      console.log('[ADD BACKEND DEBUG] No category selected');
      return;
    }
    
    console.log('[ADD BACKEND DEBUG] Category ID is valid:', categoryId);
    console.log('[ADD BACKEND DEBUG] localRule.backend_category_ids before:', this.localRule.backend_category_ids);
    
    if (!Array.isArray(this.localRule.backend_category_ids)) {
      console.log('[ADD BACKEND DEBUG] Initializing backend_category_ids array');
      this.localRule.backend_category_ids = [];
    }

    const categoriesToAdd = [categoryId];

    if (this.includeChildCategories) {
      console.log('[ADD BACKEND DEBUG] Including children');
      const childCategories = this.getChildCategoryIds(categoryId);
      console.log('[ADD BACKEND DEBUG] Child category IDs found:', childCategories);
      categoriesToAdd.push(...childCategories);
    }

    console.log('[ADD BACKEND DEBUG] Categories to add:', categoriesToAdd);

    categoriesToAdd.forEach(id => {
      if (!this.localRule.backend_category_ids.includes(id)) {
        console.log('[ADD BACKEND DEBUG] Adding category ID:', id);
        this.localRule.backend_category_ids.push(id);
      } else {
        console.log('[ADD BACKEND DEBUG] Category ID already exists:', id);
      }
    });

    console.log('[ADD BACKEND DEBUG] localRule.backend_category_ids after:', this.localRule.backend_category_ids);

    this.selectedCategoryId = null;
    this.includeChildCategories = false;
    
    this.$forceUpdate();
  },

  // Get child category IDs (not paths)
  getChildCategoryIds(parentId) {
    if (!parentId || !this.backendTaxonomyData) return [];

    const childIds = [];
    
    // Find the parent node
    const parentNode = this.findNodeById(parentId);
    if (!parentNode || !parentNode.subcategories) return [];
    
    // Recursively collect all child IDs
    function collectChildIds(node) {
      if (node.subcategories && Array.isArray(node.subcategories)) {
        node.subcategories.forEach(child => {
          if (child.id) {
            childIds.push(child.id);
            collectChildIds(child); // Recursively get all descendants
          }
        });
      }
    }
    
    collectChildIds(parentNode);
    
    // Filter out any that are already in the rule
    return childIds.filter(id => !this.localRule.backend_category_ids.includes(id));
  },
  findNodeById(nodeId) {
    function searchNode(node) {
      if (!node) return null;
      if (node.id === nodeId) return node;
      
      if (node.subcategories && Array.isArray(node.subcategories)) {
        for (const child of node.subcategories) {
          const found = searchNode(child);
          if (found) return found;
        }
      }
      return null;
    }
    
    for (const key in this.backendTaxonomyData) {
      const found = searchNode(this.backendTaxonomyData[key]);
      if (found) return found;
    }
    
    return null;
  },

  // Get display path for a category ID
  getCategoryDisplay(categoryId) {
    const info = this.categoryDisplayInfo[categoryId];
    return info ? info.path : categoryId;
  },
  // Get inherited attributes for a category ID
  getInheritedAttributeNames(categoryId) {
    if (!this.backendTaxonomyData || !categoryId) return [];

    const node = this.findNodeById(categoryId);
    if (!node) return [];

    const attributeIds = new Set();
    
    function collectAttributes(n) {
      if (n.attribute_ids && Array.isArray(n.attribute_ids)) {
        n.attribute_ids.forEach(id => attributeIds.add(id));
      }
      if (n.subcategories && Array.isArray(n.subcategories)) {
        n.subcategories.forEach(child => collectAttributes(child));
      }
    }
    collectAttributes(node);

    const attributeNames = [];
    attributeIds.forEach(id => {
      const attr = this.attributes[id];
      if (attr) {
        attributeNames.push(attr.display_name?.en || attr.name || `Attribute ${id}`);
      }
    });

    return attributeNames.sort();
  },

    // NEW: Methods for handling multiple web category codes
    isValidWebCategoryCode(code) {
  if (!code || typeof code !== 'string') return false;
  // Check if code contains only lowercase letters, numbers, hyphens, and underscores
  return /^[a-z0-9_-]+$/.test(code.trim());
},
    addWebCategoryCode() {
      const code = this.newWebCategoryCode?.trim();
      if (!code || !this.isValidWebCategoryCode(code)) return;
      
      if (!Array.isArray(this.localRule.web_category_codes)) {
        this.localRule.web_category_codes = [];
      }
      
      if (!this.localRule.web_category_codes.includes(code)) {
        this.localRule.web_category_codes.push(code);
        this.newWebCategoryCode = null;
      }
    },
    removeWebCategoryCode(index) {
      if (Array.isArray(this.localRule.web_category_codes)) {
        this.localRule.web_category_codes.splice(index, 1);
      }
    },
    removeBackendCategory(index) {
      if (Array.isArray(this.localRule.backend_category_ids)) {
        this.localRule.backend_category_ids.splice(index, 1);
      }
    },
    // END NEW

    isConditionComplete(condition) {
      if (!condition || !condition.attribute_id || !condition.operator) return false;
      
      if (condition.operator === 'is' && (condition.value_id === null || condition.value_id === undefined || condition.value_id === '')) return false;
      if (condition.operator === 'is one of' && (!Array.isArray(condition.value_id) || condition.value_id.length === 0)) return false;
      
      return true;
    },
    ensureConditions() {
      if (!this.localRule.conditions) {
          this.localRule.conditions = { operator: 'AND', groups: [] };
      }
      if (!Array.isArray(this.localRule.conditions.groups) || this.localRule.conditions.groups.length === 0) {
        this.addGroup();
      }
    },
    createCondition() {
      return {
        id: `cond_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        attribute_id: null,
        operator: 'is',
        value_id: null,
        value: undefined
      };
    },
    createEmptyGroup() {
      return {
        id: `group_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        operator: 'OR',
        conditions: [this.createCondition()]
      };
    },
    addCondition(group) {
      if (!Array.isArray(group.conditions)) {
        group.conditions = [];
      }
      group.conditions.push(this.createCondition());
      this.$forceUpdate();
    },
    removeCondition(groupIndex, condIndex) {
      if (this.localRule.conditions?.groups?.[groupIndex]) {
        const group = this.localRule.conditions.groups[groupIndex];

        if (Array.isArray(group.conditions)) {
          group.conditions.splice(condIndex, 1);

          if (group.conditions.length === 0) {
            this.removeGroup(groupIndex);
          } else {
            this.$forceUpdate();
          }
        }
      }
    },
    addGroup() {
      if (!this.localRule.conditions || !Array.isArray(this.localRule.conditions.groups)) {
          this.localRule.conditions = { operator: 'AND', groups: [] };
      }
      this.localRule.conditions.groups.push(this.createEmptyGroup());
      this.$forceUpdate();
    },
    removeGroup(groupIndex) {
      if (Array.isArray(this.localRule.conditions.groups)) {
        this.localRule.conditions.groups.splice(groupIndex, 1);
        this.$forceUpdate();
      }
    },
    onAttributeChange(condition) {
      console.log('onAttributeChange called', condition);
      condition.value_id = null;
    },
    onOperatorChange(condition) {
      console.log('onOperatorChange called', condition);
      if (condition.operator === 'is one of' || condition.operator === 'in') {
        condition.value_id = Array.isArray(condition.value_id) ? condition.value_id : (condition.value_id ? [condition.value_id] : []);
      } else {
        condition.value_id = Array.isArray(condition.value_id) ? (condition.value_id.length > 0 ? condition.value_id[0] : null) : condition.value_id;
      }
    },
    onWebCategoryCodeChange(newValue) {
    // Store the value but don't auto-add yet
    // The menu state change will handle the auto-add logic
  },
  
  onMenuStateChange(isOpen) {
    if (!isOpen && this.menuWasOpen) {
      // Menu just closed - check if we have a valid selection
      if (this.newWebCategoryCode && this.availableWebCategoryCodes.includes(this.newWebCategoryCode)) {
        this.$nextTick(() => {
          this.addWebCategoryCode();
        });
      }
    }
    this.menuWasOpen = isOpen;
  },
    getValueItems(attributeId) {
      if (!attributeId || !this.attributes[attributeId]) return [];
      const attr = this.attributes[attributeId];

      // Handle boolean attributes - use simple True/False strings
      if (attr.data_type === 'boolean') {
        return [
          { title: 'True', value: 'True' },
          { title: 'False', value: 'False' }
        ];
      }

      if (attr.values && typeof attr.values === 'object') {
        return Object.entries(attr.values).map(([valueId, valueObj]) => ({
          title: valueObj.value_en || `(Unnamed value)`, 
          value: valueId,
        })).sort((a, b) => a.title.localeCompare(b.title));
      }
      return [];
    },
    addValueToCondition(condition, valueId) {
      if (!valueId) return;
      if (!condition.value_id) condition.value_id = [];
      if (!condition.value_id.includes(valueId)) {
        condition.value_id.push(valueId);
      }
      this.tempValue = null;
    },
    removeValueFromCondition(condition, index) {
      if (condition.value_id) {
        condition.value_id.splice(index, 1);
      }
    },
    getValueLabel(attributeId, valueId) {
      const items = this.getValueItems(attributeId);
      const item = items.find(i => i.value === valueId);
      return item ? item.title : valueId;
    },
    onBackendCategorySelected(selectedPath) {
      if (selectedPath) {
        this.newBackendCategoryPath = selectedPath;
      }
    },
    cancel() {
      this.$emit('update:show', false);
    },
    save() {
      if (!this.isRuleValid) return;

      console.log('[SAVE DEBUG] localRule.backend_category_ids before processing:', 
        JSON.stringify(this.localRule.backend_category_ids));

      const ruleToSave = JSON.parse(JSON.stringify(this.localRule));

      console.log('[SAVE DEBUG] ruleToSave.backend_category_ids after deep copy:', 
        JSON.stringify(ruleToSave.backend_category_ids));

      if (!ruleToSave.id) {
          ruleToSave.id = `${ruleToSave.type}_${Date.now()}`;
      }

      // Clean up conditions
      if (ruleToSave.conditions && Array.isArray(ruleToSave.conditions.groups)) {
          ruleToSave.conditions.groups.forEach(group => {
              if (Array.isArray(group.conditions)) {
                group.conditions = group.conditions.filter(this.isConditionComplete);
              }
          });
          ruleToSave.conditions.groups = ruleToSave.conditions.groups.filter(group =>
            Array.isArray(group.conditions) && group.conditions.length > 0
          );

          if (ruleToSave.conditions.groups.length === 0) {
              delete ruleToSave.conditions;
          } else {
              ruleToSave.conditions.groups.forEach(group => {
                if (Array.isArray(group.conditions)) {
                  group.conditions.forEach(condition => {
                      condition.operator = condition.operator === 'is' ? 'equals' : 'in';
                      delete condition.value;
                  });
                }
              });
          }
      }

      console.log('[SAVE DEBUG] ruleToSave.backend_category_ids before cleanup:', 
        JSON.stringify(ruleToSave.backend_category_ids));

      // Clean up empty arrays - BUT ONLY DELETE IF ACTUALLY EMPTY
      if (Array.isArray(ruleToSave.backend_category_ids)) {
        if (ruleToSave.backend_category_ids.length === 0) {
          console.log('[SAVE DEBUG] Deleting empty backend_category_ids array');
          delete ruleToSave.backend_category_ids;
        } else {
          console.log('[SAVE DEBUG] Keeping backend_category_ids with', ruleToSave.backend_category_ids.length, 'items');
        }
      } else {
        console.log('[SAVE DEBUG] backend_category_ids is not an array:', typeof ruleToSave.backend_category_ids);
      }

      // UPDATED: Clean up empty web_category_codes array
      if (Array.isArray(ruleToSave.web_category_codes)) {
        if (ruleToSave.web_category_codes.length === 0) {
          delete ruleToSave.web_category_codes;
        }
      }

      console.log('[SAVE DEBUG] Final ruleToSave.backend_category_ids:', 
        JSON.stringify(ruleToSave.backend_category_ids));
      console.log('[SAVE DEBUG] Complete final rule:', JSON.stringify(ruleToSave, null, 2));

      this.$emit('save', ruleToSave);
      this.cancel();
    },
  },
};
</script>

<style scoped>
.v-autocomplete .v-field {
  contain: layout;
}
.v-autocomplete {
  contain: layout;
}

.v-autocomplete .v-overlay__content {
  contain: layout style paint;
}

.v-autocomplete .v-list {
  overflow-y: auto !important;
  max-height: 200px !important;
}

.v-menu .v-list {
  contain: layout;
}

.condition-group {
    border: 1px solid #E0E0E0;
    border-radius: 8px;
    background-color: #FAFAFA;
    position: relative;
}
.condition-row {
    min-height: 56px;
}
.category-item {
    border: 1px solid #E0E0E0;
    border-radius: 4px;
    background-color: #F9F9F9;
}
.gap-2 {
    gap: 0.5rem;
}

.stable-chips {
  contain: layout style size;
}

.stable-chips :deep(.v-field__input) {
  contain: layout;
  will-change: auto;
}
</style>